import {
  AlignmentEnumAlignment as V2ImageAlignment,
  ImageFit as V2ImageFit,
  ImagePosition as V2ImagePosition,
  type MediaItem,
} from '@wix/ambassador-forms-v4-form/types';

import {
  type Image,
  ImageAlignment,
  ImageFit,
  ImagePosition,
  type MediaSettings,
} from '../types';

type QuestionImageLabelMediaView = {
  questionImage?: Image;
  questionImageAltText?: string;
  questionImagePlacement?: ImagePosition;
  questionImageResize?: ImageFit;
  questionImageAlignment?: ImageAlignment;
};

export const toV1ImagePlacement = (
  imagePosition?: V2ImagePosition,
): ImagePosition =>
  imagePosition === V2ImagePosition.BELOW
    ? ImagePosition.BOTTOM
    : ImagePosition.TOP;

export const toV2ImagePosition = (
  imagePlacement: ImagePosition,
): V2ImagePosition =>
  imagePlacement === ImagePosition.BOTTOM
    ? V2ImagePosition.BELOW
    : V2ImagePosition.ABOVE;

export const toV1ImageFit = (imageFit?: V2ImageFit): ImageFit =>
  imageFit === V2ImageFit.CONTAIN ? ImageFit.CONTAIN : ImageFit.COVER;

export const toV2ImageFit = (imageFit: ImageFit): V2ImageFit =>
  imageFit === ImageFit.CONTAIN ? V2ImageFit.CONTAIN : V2ImageFit.COVER;

export const toV1ImageAlignment = (
  alignment?: V2ImageAlignment,
): ImageAlignment => {
  if (alignment === V2ImageAlignment.LEFT) {
    return ImageAlignment.LEFT;
  }

  if (alignment === V2ImageAlignment.RIGHT) {
    return ImageAlignment.RIGHT;
  }

  return ImageAlignment.CENTER;
};

export const toV2ImageAlignment = (
  alignment: ImageAlignment,
): V2ImageAlignment => {
  if (alignment === ImageAlignment.LEFT) {
    return V2ImageAlignment.LEFT;
  }

  if (alignment === ImageAlignment.RIGHT) {
    return V2ImageAlignment.RIGHT;
  }

  return V2ImageAlignment.CENTER;
};

type FieldLabelMediaProperties = {
  media?: Image;
  mediaSettings?: MediaSettings;
};

export const toV2LabelMedia = (image?: Image): MediaItem | undefined =>
  image
    ? {
        image: {
          id: image.id,
          width: image.width,
          height: image.height,
          altText: image.altText,
        },
      }
    : undefined;

export const fromV2LabelMedia = (media?: MediaItem): Image | undefined => {
  const image = media?.image;

  if (!image?.id) {
    return undefined;
  }

  return {
    id: image.id,
    width: image.width ?? 0,
    height: image.height ?? 0,
    altText: image.altText ?? undefined,
  };
};

export function createFieldLabelMediaProperties(
  view: QuestionImageLabelMediaView,
): FieldLabelMediaProperties {
  if (!view.questionImage) {
    return {};
  }

  const mediaSettings = toFieldLabelMediaSettings(view);

  return {
    media: {
      ...view.questionImage,
      ...(view.questionImageAltText !== undefined && {
        altText: view.questionImageAltText,
      }),
    },
    ...(mediaSettings && { mediaSettings }),
  };
}

function toFieldLabelMediaSettings(
  view: QuestionImageLabelMediaView,
): MediaSettings | undefined {
  const mediaSettings: MediaSettings = {
    ...(view.questionImagePlacement && {
      imagePosition: view.questionImagePlacement as ImagePosition,
    }),
    ...(view.questionImageResize && {
      imageFit: view.questionImageResize,
    }),
    ...(view.questionImageAlignment && {
      imageAlignment: view.questionImageAlignment,
    }),
  };

  return Object.keys(mediaSettings).length ? mediaSettings : undefined;
}
